import { useState } from "react";
import { useTranslation } from "react-i18next";
import { extractErrorMessage } from "@/shared/utils";
import {
  useUploadAttachedDocumentMutation,
  useUpdateAttachedDocumentMutation,
  useDeleteAttachedDocumentMutation,
} from "../api";
import type { AttachedDocumentCreateRequest, AttachedDocumentUpdateRequest } from "../types";
import type { PromptState } from "./useAttachedDocumentsModals";

export function useAttachedDocumentsMutations(setPrompt: (state: PromptState | null) => void) {
  const { t } = useTranslation("AttachedDocumentsPage");
  const [uploadDoc] = useUploadAttachedDocumentMutation();
  const [updateDoc] = useUpdateAttachedDocumentMutation();
  const [deleteDoc] = useDeleteAttachedDocumentMutation();

  const [uploading, setUploading] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const isUpdating = updatingId !== null;
  const isDeleting = deletingId !== null;

  async function handleUpload(dto: AttachedDocumentCreateRequest) {
    if (uploading) return;
    try {
      setUploading(true);
      await uploadDoc(dto).unwrap();
      setPrompt({
        title: t("messages.uploadSuccessTitle"),
        text: t("messages.uploadSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.uploadErrorText", t),
        variant: "error",
      });
      throw err;
    } finally {
      setUploading(false);
    }
  }

  async function handleUpdate(id: number, dto: AttachedDocumentUpdateRequest) {
    if (isUpdating) return;
    try {
      setUpdatingId(id);
      await updateDoc({ id, data: dto }).unwrap();
      setPrompt({
        title: t("messages.updateSuccessTitle"),
        text: t("messages.updateSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.updateErrorText", t),
        variant: "error",
      });
    } finally {
      setUpdatingId(null);
    }
  }

  async function handleDelete(id: number) {
    if (isDeleting) return;
    try {
      setDeletingId(id);
      await deleteDoc(id).unwrap();
      setPrompt({
        title: t("messages.deleteSuccessTitle"),
        text: t("messages.deleteSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.deleteErrorText", t),
        variant: "error",
      });
    } finally {
      setDeletingId(null);
    }
  }

  return {
    uploading,
    updatingId,
    deletingId,
    isUpdating,
    isDeleting,
    handleUpload,
    handleUpdate,
    handleDelete,
  };
}
